import React, { useEffect, useMemo, useRef, type ReactNode } from 'react';
import { getTheme, useThemeMode } from '../theme';
import { resolveUrl } from '../api';
import type { VoiceMessage } from '../q/huntQ';

/** Rad som Q hört (samma form som jaktens transkription i KartaTab). */
export type QHeardEntry = { speaker: string; text: string; ts: number; lat?: number; lng?: number };

type Item =
  | { kind: 'heard'; ts: number; entry: QHeardEntry }
  | { kind: 'insight'; ts: number; id: string; node: ReactNode }
  | { kind: 'voice'; ts: number; voice: VoiceMessage };

/**
 * QJ1: Q-fliken under jakt – det Q hört (alla deltagares transkription och skott), Q:s slutsatser
 * (observationer, skott, drev; med knappar för att registrera fällt vilt) och röstmeddelanden via Q.
 * Slutsatskorten ritas av KartaTab (renderInsight) eftersom registreringen bor där.
 */
export function QHuntLog({ heard, insights, voice, listening, summary, onPlayVoice, shotLabel }: {
  heard: QHeardEntry[];
  insights: { id: string; ts: number; node: ReactNode }[];
  voice: VoiceMessage[];
  /** Lyssnar telefonen just nu (transkriptionen igång)? */
  listening: boolean;
  summary?: string | null;
  onPlayVoice: (v: VoiceMessage) => void;
  /** Texten som skottraderna har (SHOT_EVENT_TEXT). */
  shotLabel: string;
}) {
  const { mode } = useThemeMode();
  const T = getTheme(mode);
  const bottomRef = useRef<HTMLDivElement>(null);
  const items = useMemo<Item[]>(() => [
    ...heard.map(entry => ({ kind: 'heard' as const, ts: entry.ts, entry })),
    ...insights.map(i => ({ kind: 'insight' as const, ts: i.ts, id: i.id, node: i.node })),
    ...voice.map(v => ({ kind: 'voice' as const, ts: v.ts, voice: v })),
  ].sort((a, b) => a.ts - b.ts), [heard, insights, voice]);
  useEffect(() => { bottomRef.current?.scrollIntoView({ block: 'end' }); }, [items.length]);
  const time = (ts: number) => new Date(ts).toLocaleTimeString('sv-SE', { hour: '2-digit', minute: '2-digit' });

  return (
    <div data-q-hunt-log style={{ flex: 1, minHeight: 0, overflowY: 'auto', padding: '10px 12px', display: 'flex', flexDirection: 'column', gap: 8 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 11.5, color: T.textMuted, lineHeight: 1.4 }}>
        <span aria-hidden style={{ width: 8, height: 8, borderRadius: 4, flexShrink: 0, background: listening ? '#61D836' : T.border }} />
        <span>
          {listening ? 'Q lyssnar efter skott, drev och observationer.' : 'Q lyssnar inte just nu.'} Säg "Lasse till Ola …" för att skicka ett röstmeddelande till Ola.
        </span>
      </div>
      {summary && (
        <div style={{ borderRadius: 10, padding: '8px 10px', background: 'rgba(80,120,200,0.15)', border: '1px solid rgba(120,160,255,0.35)', fontSize: 12.5, lineHeight: 1.45, whiteSpace: 'pre-wrap' }}>
          <div style={{ fontSize: 10, fontWeight: 800, color: '#a8c4ff', marginBottom: 4 }}>Sammanfattning</div>
          {summary}
        </div>
      )}
      {items.length === 0 && <div style={{ color: T.textMuted, fontSize: 12 }}>Inget hört än.</div>}
      {items.map((it, i) => {
        if (it.kind === 'insight') return <React.Fragment key={`i:${it.id}`}>{it.node}</React.Fragment>;
        if (it.kind === 'voice') {
          const v = it.voice;
          return (
            <div key={`v:${v.id}`} data-q-voice style={{ alignSelf: v.toMe ? 'flex-start' : 'flex-end', maxWidth: '92%', borderRadius: 10, padding: '7px 10px', background: v.toMe ? 'rgba(97,216,54,0.14)' : T.surface2, border: `1px solid ${v.toMe ? 'rgba(97,216,54,0.45)' : T.border}`, fontSize: 12.5 }}>
              <div style={{ fontSize: 10, fontWeight: 800, color: v.toMe ? '#61D836' : T.textSub, marginBottom: 3 }}>
                {v.toMe ? `${v.from} → dig` : `Du → ${v.to}`} · {time(v.ts)}{v.toMe && !v.heard ? ' · ny' : ''}
              </div>
              {v.text && <div style={{ lineHeight: 1.4 }}>{v.text}</div>}
              {(v.audioUrl || v.text) && v.toMe && (
                <button type="button" onClick={() => onPlayVoice(v)} style={{ marginTop: 5, padding: '4px 10px', borderRadius: 7, border: `1px solid ${T.border}`, background: 'transparent', color: T.text, fontSize: 11, fontWeight: 700, cursor: 'pointer', fontFamily: 'inherit' }}>
                  ▶ Spela upp
                </button>
              )}
              {v.audioUrl && !v.toMe && <audio src={resolveUrl(v.audioUrl)} controls preload="none" style={{ display: 'block', height: 28, marginTop: 4, maxWidth: '100%' }} />}
            </div>
          );
        }
        const e = it.entry;
        const shot = e.text === shotLabel;
        return (
          <div key={`h:${e.ts}:${e.speaker}:${i}`} style={{ fontSize: 12, lineHeight: 1.4, color: shot ? '#ff8a80' : T.textSub }}>
            <span style={{ color: T.textMuted, fontSize: 10, marginRight: 6 }}>{time(e.ts)}</span>
            <b style={{ color: T.text, fontWeight: 700 }}>{e.speaker}:</b> {shot ? '💥 skott hört' : e.text}
          </div>
        );
      })}
      <div ref={bottomRef} />
    </div>
  );
}
